<!--@format-->
<template>
    <div class="verify-bank-account-widget" :class="componentClasses">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>{{ isLoading ? 'Loading...' : 'Verify Account' }}</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="isLoading">
                    <loading-screen-svg />
                </div>
                <div v-else class="widget-container">
                    <div class="payment edit">
                        <div class="form">
                            <app-input
                                v-model="firstDeposit"
                                label="First deposit (In cents, e.g. 47 = $0.47)"
                                placeholder="00"
                                type="number"
                                :is-valid="getIsValidField('firstDeposit')"
                                :class="{ 'has-errors': !getIsValidField('firstDeposit') }"
                                error="Required"
                            />
                            <app-input
                                v-model="secondDeposit"
                                label="Second deposit (In cents, e.g. 47 = $0.47)"
                                placeholder="00"
                                type="number"
                                :is-valid="getIsValidField('secondDeposit')"
                                :class="{ 'has-errors': !getIsValidField('secondDeposit') }"
                                error="Required"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn" :disabled="processing" @click="closeWidget">
                        Cancel
                    </app-button>
                </div>
                <app-button class="save-btn" :loading="processing" @click="verifyData">
                    Verify
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const AppInput = () => import('@/components/common/app-input');
import { responsiveHelpers } from '@/scripts/helpers';
import { PaymentMethodsService } from '@/scripts/services/payment-methods/payment-methods-service';
import EventBus from '@/scripts/event-bus.js';
import { ResponsiveMixin } from 'vue-responsive-components';
import { numeric, required } from 'vuelidate/lib/validators';

export default {
    name: 'verify-bank-account-widget',

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'loading-screen-svg': LoadingScreenSvg,
        'app-input': AppInput,
    },

    mixins: [ResponsiveMixin],

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: false,
            processing: false,
            firstDeposit: null,
            secondDeposit: null,
            clientId: this.widget.clientId,
            agencyId: this.widget.agencyId,
            paymentMethodId: this.widget.paymentMethodId,
        };
    },

    computed: {
        componentClasses() {
            return [responsiveHelpers.getBreakpointClasses(this)];
        },

        userConfig() {
            if (this.clientId) {
                return { userType: 'client', id: this.clientId };
            } else if (this.agencyId) {
                return { userType: 'agency', id: this.agencyId };
            } else {
                return { userType: 'masterAgency', id: this.agencyId };
            }
        },
    },

    breakpoints: responsiveHelpers.getBreakpoints(),

    validations() {
        return {
            firstDeposit: {
                required,
                numeric,
            },

            secondDeposit: {
                required,
                numeric,
            },
        };
    },

    methods: {
        getIsValidField(field) {
            const isInvalidValid = this.$v[field].$invalid && this.$v[field].$dirty;
            return !isInvalidValid;
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        verifyData() {
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            }

            const request = {
                microDeposits: [parseInt(this.firstDeposit), parseInt(this.secondDeposit)],
                paymentMethodId: this.paymentMethodId,
            };
            this.processing = true;
            PaymentMethodsService.verify(request, this.userConfig.id, this.userConfig.userType)
                .then(() => {
                    this.$helpers.showSuccessNotification('Account verified successfully.');
                    EventBus.$emit('payment-method:update', true);
                    this.closeWidget();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while verifying your account.',
                        { error },
                    );
                })
                .finally(() => {
                    this.processing = false;
                });
        },
    },
};
</script>

<style>
.verify-bank-account-widget {
    color: #382153;
}

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}
input[type='number'] {
    appearance: textfield;
}

.verify-bank-account-widget .save-btn {
    width: 128px;
    height: 40px;
}

.verify-bank-account-widget .form > *:not(:first-child) {
    margin-top: 20px;
}

.verify-bank-account-widget .cancel-btn {
    background: transparent;
    border: 1px solid #cac6cb;
    font: normal normal medium 14px/24px Roboto;
    color: #877d88;
    min-width: 128px;
    min-height: 40px;
}

.verify-bank-account-widget .cancel-btn:hover {
    background: #e9e9ef;
}

.verify-bank-account-widget label {
    display: block;
    font: 500 16px/1em Roboto;
    margin-bottom: 7px;
    color: #382153;
}

.verify-bank-account-widget .app-input.error.has-errors input,
.verify-bank-account-widget .gray-container.has-errors {
    border: 2px solid #f30070;
}

.verify-bank-account-widget .app-input.error label {
    color: #382153;
}
</style>
